import type { Editor } from '@tiptap/core';
import { NodeSelection } from '@tiptap/pm/state';
import { useEditorState } from '@tiptap/react';
import { BubbleMenu, type BubbleMenuProps } from '@tiptap/react/menus';
import { css } from 'aphrodite';
import * as React from 'react';
import { Button } from '../../../../web/components-v2/Button';
import { Input } from '../../../../web/components-v2/Input';
import { setSelectedImageAlt } from './imageCommands';
import { toMarkdownLabel } from './issueMarkdown';
import { githubIssueEditorStyles as styles } from './styles';

const isImageSelected: NonNullable<BubbleMenuProps['shouldShow']> = ({ state }) =>
	state.selection instanceof NodeSelection && state.selection.node.type.name === 'image';

interface IssueImageMenuProps {
	editor: Editor;
	containerRef: React.RefObject<HTMLDivElement>;
	scrollTarget: HTMLElement | null;
}

/** Shows while an image is selected: describe what it shows, or remove it. Dragging moves it. */
export const IssueImageMenu: React.FC<IssueImageMenuProps> = ({ editor, containerRef, scrollTarget }) => {
	const image = useEditorState({
		editor,
		selector: ({ editor: current }) => {
			const { state } = current;
			const selected = state.selection instanceof NodeSelection ? state.selection.node : null;
			return { alt: selected?.type.name === 'image' ? String(selected.attrs.alt ?? '') : '' };
		},
	});
	const appendTo = React.useCallback(() => containerRef.current ?? document.body, [containerRef]);
	const options = React.useMemo<NonNullable<BubbleMenuProps['options']>>(
		() => ({
			placement: 'top',
			offset: 8,
			flip: true,
			shift: { padding: 8 },
			...(scrollTarget ? { scrollTarget } : {}),
		}),
		[scrollTarget]
	);

	return (
		<BubbleMenu
			editor={editor}
			pluginKey='githubIssueImageMenu'
			appendTo={appendTo}
			shouldShow={isImageSelected}
			options={options}
			className={css(styles.imageMenu)}
		>
			<Input
				aria-label='Image description'
				placeholder='Describe what this image shows'
				styles={[styles.imageMenuInput]}
				value={image?.alt ?? ''}
				onChange={event => setSelectedImageAlt(editor, toMarkdownLabel(event.target.value))}
				onKeyDown={event => {
					if (event.key === 'Enter' || event.key === 'Escape') {
						event.preventDefault();
						editor.commands.focus();
					}
				}}
			/>
			<Button
				variant='ghost'
				size='sm'
				styles={[styles.toolbarButton, styles.imageMenuDelete]}
				onClick={() => editor.chain().focus().deleteSelection().run()}
				aria-label='Delete image'
			>
				Delete
			</Button>
		</BubbleMenu>
	);
};
